import type { ApiKeyField } from "../types/integration";

// The values an API-key connect form starts with: each field's seeded default
// (e.g. a mail port), or nothing. Keyed by field name, as the form submits.
export const initialApiKeyValues = (fields: ReadonlyArray<ApiKeyField>): Record<string, string> =>
  Object.fromEntries(fields.filter((f) => f.defaultValue).map((f) => [f.name, f.defaultValue ?? ""]));

// Heuristic: which fields hold a secret (mask + reveal toggle) vs plain config.
// Composio types every field as "string", so we key off the name/label.
const SECRET_HINT = /key|token|secret|password|pat|credential/i;
export const isSecretField = (f: ApiKeyField): boolean => SECRET_HINT.test(f.name) || SECRET_HINT.test(f.displayName);

export const fieldPlaceholder = (f: ApiKeyField): string => f.placeholder ?? `Enter ${f.displayName.toLowerCase()}`;

export type ApiKeyConnectCopy = { lead: string; body: string; cta: string };

// What the detail page says about connecting with typed-in credentials. Email is
// its own case: it is a mailbox password, not a generated key, and it is kept on
// ZeroToClaw's side precisely so it never sits on the instance.
export const apiKeyConnectCopy = (slug: string, title: string): ApiKeyConnectCopy =>
  slug === "imap"
    ? {
        lead: "Mailbox password.",
        body:
          "Enter your mail server settings and the mailbox password, as in your email app. ZeroToClaw keeps the " +
          "password encrypted on its own servers, never on your instance, and never shows it to your agent.",
        cta: "Connect your mailbox with its server settings and password so your agent can read, search and send email.",
      }
    : {
        lead: "API key.",
        body: `Generate a key in ${title} and paste it in. It's stored encrypted on your instance and never shown to your agent.`,
        cta: `Connect ${title} with an API key so your agent can use it.`,
      };

// The form's rows: a field marked inlineWithPrevious sits beside the one before
// it (a server and its port) instead of below it.
export const groupFieldRows = (fields: ReadonlyArray<ApiKeyField>): ApiKeyField[][] =>
  fields.reduce<ApiKeyField[][]>((rows, field) => {
    const last = rows[rows.length - 1];
    if (field.inlineWithPrevious && last) last.push(field);
    else rows.push([field]);
    return rows;
  }, []);
